import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { getPaymentStatus, paypalCapture, paypalSubConfirm } from "../lib/api";
import { track } from "../lib/track";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const [state, setState] = useState("checking");
  const [downloadLink, setDownloadLink] = useState(null);
  const [kind, setKind] = useState("product");
  const [itemName, setItemName] = useState(null);

  useEffect(() => {
    if (provider === "paypal") {
      const token = params.get("token");
      const subId = params.get("subscription_id");
      if (!token && !subId) { setState("error"); return; }
      const run = subId ? () => paypalSubConfirm(subId) : () => paypalCapture(token);
      run()
        .then((d) => {
          setDownloadLink(d.download_link || null);
          setKind(d.kind || "product"); setItemName(d.item_name || null);
          if (d.payment_status === "paid") { setState("paid"); track("purchase"); }
          else setState("pending");
        })
        .catch(() => setState("error"));
      return;
    }
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setDownloadLink(data.download_link || null);
          setKind(data.kind || "product"); setItemName(data.item_name || null);
          setState("paid"); track("purchase"); return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (tries++ < 8) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <Shell testid="payment-success">
      {state === "checking" && <><Loader2 className="animate-spin text-[var(--petrol)]" size={48} /><H>Verifica del pagamento…</H></>}
      {state === "paid" && <>
        <CheckCircle2 className="text-[var(--petrol)]" size={56} strokeWidth={1.25} />
        {kind === "academy" ? <>
          <H>Iscrizione confermata</H>
          <P>Grazie per esserti iscritto a <strong>{itemName || "al programma"}</strong>. Riceverai via email tutti i dettagli della prossima edizione: calendario delle sessioni, materiali e accessi.</P>
        </> : kind === "membership" ? <>
          <H>Benvenuta/o nell'Executive Club</H>
          <P>La tua membership è attiva. Riceverai via email i dettagli di accesso ai contenuti riservati.</P>
        </> : <>
          <H>Pagamento completato</H>
          {downloadLink
            ? <>
                <P>Grazie per il tuo acquisto. Il tuo download è pronto (link valido 72h, max 3 download). Te l'abbiamo inviato anche via email.</P>
                <a href={downloadLink} data-testid="success-download-btn"
                   className="btn-fill mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-8 py-4">
                  <Download size={16} strokeWidth={1.75} /> Vai al download
                </a>
              </>
            : <P>Grazie per il tuo acquisto. Riceverai i dettagli via email.</P>}
        </>}
      </>}
      {state === "pending" && <><Loader2 className="animate-spin text-[var(--petrol)]" size={48} /><H>Pagamento in elaborazione</H><P>Aggiorna tra poco per lo stato definitivo.</P></>}
      {state === "error" && <><XCircle className="text-red-500" size={56} strokeWidth={1.25} /><H>Qualcosa è andato storto</H><P>Nessun addebito confermato. Riprova dal sito.</P></>}
      <Home />
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel">
      <XCircle className="text-[var(--ink-soft)]" size={56} strokeWidth={1.25} />
      <H>Pagamento annullato</H>
      <P>Nessun addebito effettuato. Puoi riprovare quando vuoi.</P>
      <Home />
    </Shell>
  );
}

const Shell = ({ children, testid }) => (
  <div data-testid={testid} className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-white">
    {children}
  </div>
);
const H = ({ children }) => <h1 className="font-serif text-4xl text-[var(--ink)] mt-6">{children}</h1>;
const P = ({ children }) => <p className="text-[var(--ink-soft)] mt-3 max-w-md">{children}</p>;
const Home = () => (
  <Link to="/" data-testid="back-home" className="btn-fill mt-8 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-8 py-4">
    Torna al sito
  </Link>
);
